import type { TimerConfig } from 'model';
import type { SoundOption } from 'utils/audio';
import { playSoundOption } from 'utils/audio';

interface TimerSettingsProps {
  config: TimerConfig;
  onChange: (config: TimerConfig) => void;
  disabled: boolean;
}

const soundOptions: { value: SoundOption; label: string }[] = [
  { value: 'triple-beep', label: 'Triple Beep' },
  { value: 'double-beep', label: 'Double Beep' },
  { value: 'single-beep', label: 'Single Beep' },
  { value: 'rising-tone', label: 'Rising Tone' },
  { value: 'descending-tone', label: 'Descending Tone' },
  { value: 'low-beep', label: 'Low Beep' },
  { value: 'high-beep', label: 'High Beep' },
  { value: 'bell', label: 'Bell' }
];

export function TimerSettings({ config, onChange, disabled }: TimerSettingsProps): JSX.Element {
  const handleSetDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.valueAsNumber;
    onChange({ ...config, setDuration: Number.isNaN(value) ? 1 : Math.max(1, value) });
  };

  const handleIntervalDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.valueAsNumber;
    onChange({ ...config, intervalDuration: Number.isNaN(value) ? 0 : Math.max(0, value) });
  };

  const handleTotalSetsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.valueAsNumber;
    if (Number.isNaN(value)) {
      onChange({ ...config, totalSets: undefined });
    } else {
      // Allow -1 for infinite sets
      onChange({ ...config, totalSets: value === -1 ? -1 : Math.max(1, value) });
    }
  };

  const handleWarningTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.valueAsNumber;
    onChange({ ...config, warningTime: Number.isNaN(value) ? 0 : Math.max(0, value) });
  };

  const handleCompletionSoundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...config, playCompletionSound: e.target.checked });
  };

  const handleWarningSoundChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...config, warningSound: e.target.value as SoundOption });
  };

  const handleSetEndSoundChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...config, setEndSound: e.target.value as SoundOption });
  };

  const handlePlayWarningSound = () => {
    playSoundOption(config.warningSound);
  };

  const handlePlaySetEndSound = () => {
    playSoundOption(config.setEndSound);
  };

  return (
    <div className="timer-settings">
      <div className="timer-setting-row timer-setting-row-full">
        <label htmlFor="setDuration">Set Duration (seconds)</label>
        <input
          id="setDuration"
          type="number"
          className="form-control"
          value={config.setDuration}
          onChange={handleSetDurationChange}
          disabled={disabled}
          min={1}
        />
      </div>

      <div className="timer-setting-row timer-setting-row-full">
        <label htmlFor="intervalDuration">Rest Interval (seconds)</label>
        <input
          id="intervalDuration"
          type="number"
          className="form-control"
          value={config.intervalDuration || ''}
          onChange={handleIntervalDurationChange}
          onBlur={(e) => {
            if (e.target.value === '') {
              onChange({ ...config, intervalDuration: 0 });
            }
          }}
          disabled={disabled}
          min={0}
          placeholder="0"
        />
      </div>

      <div className="timer-setting-row timer-setting-row-full">
        <label htmlFor="totalSets">
          Number of Sets <span className="text-muted">(-1 for infinite)</span>
        </label>
        <input
          id="totalSets"
          type="number"
          className="form-control"
          value={config.totalSets ?? ''}
          onChange={handleTotalSetsChange}
          disabled={disabled}
          min={-1}
          placeholder="∞"
        />
      </div>

      <div className="timer-setting-row timer-setting-row-full">
        <label htmlFor="warningTime">Warning Time (seconds before end)</label>
        <input
          id="warningTime"
          type="number"
          className="form-control"
          value={config.warningTime || ''}
          onChange={handleWarningTimeChange}
          onBlur={(e) => {
            if (e.target.value === '') {
              onChange({ ...config, warningTime: 0 });
            }
          }}
          disabled={disabled}
          min={0}
          placeholder="0"
        />
      </div>

      <div className="timer-setting-row">
        <label htmlFor="warningSound">Warning Sound</label>
        <div className="timer-sound-control">
          <select
            id="warningSound"
            className="form-control"
            value={config.warningSound}
            onChange={handleWarningSoundChange}
            disabled={disabled}
          >
            {soundOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="timer-play-btn"
            onClick={handlePlayWarningSound}
            disabled={disabled}
            title="Play warning sound"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="timer-setting-row">
        <label htmlFor="setEndSound">End Sound</label>
        <div className="timer-sound-control">
          <select
            id="setEndSound"
            className="form-control"
            value={config.setEndSound}
            onChange={handleSetEndSoundChange}
            disabled={disabled}
          >
            {soundOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="timer-play-btn"
            onClick={handlePlaySetEndSound}
            disabled={disabled}
            title="Play set end sound"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="timer-setting-row timer-setting-checkbox">
        <label htmlFor="playCompletionSound">
          <input
            id="playCompletionSound"
            type="checkbox"
            checked={config.playCompletionSound}
            onChange={handleCompletionSoundChange}
            disabled={disabled}
          />
          <span>Play sound when routine completes</span>
        </label>
      </div>
    </div>
  );
}
